import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import API from '../api/axios';
import { formatRole } from '../utils/roleLabel';
import './HODDashboard.css';

const BookIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);


const UsersIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
  </svg>
);

const ClipboardIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
  </svg>
);

const FileTextIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const HODDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalSubjects: 0,
    activeSubjects: 0,
    totalFaculty: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subjRes, userRes] = await Promise.all([
          API.get(`/subjects?department_id=${user.department_id}&limit=1`).catch(() => ({ data: { total: 0 } })),
          API.get('/auth/hod-users?limit=1').catch(() => ({ data: { total: 0 } }))
        ]);

        setStats({
          totalSubjects: subjRes.data.total ?? 0,
          activeSubjects: 0,
          totalFaculty: userRes.data.total ?? 0
        });
      } catch {
        console.error('Failed to fetch dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user.department_id]);

  if (loading) {
    return (
      <div className="hod-spinner-wrap">
        <div className="hod-spinner"></div>
      </div>
    );
  }

  return (
    <div className="hod-page">
      <div className="hod-page-header">
        <h1>Head of Department Dashboard</h1>
        <p>Welcome back, {user.name} ({formatRole(user.role)})! Oversee subject curricula, review pending registrations, and manage question templates.</p>
      </div>

      {/* Stats Cards */}
      <div className="hod-stats-grid">
        <div className="hod-stat-card" onClick={() => navigate('/hod/subjects')}>
          <div className="hod-stat-icon hod-stat-icon--blue"><BookIcon /></div>
          <div>
            <div className="hod-stat-value">{stats.totalSubjects}</div>
            <div className="hod-stat-label">Total Subjects</div>
          </div>
        </div>
        <div className="hod-stat-card" onClick={() => navigate('/hod/subjects')}>
          <div className="hod-stat-icon hod-stat-icon--green"><CheckCircleIcon /></div>
          <div>
            <div className="hod-stat-value">{stats.activeSubjects}</div>
            <div className="hod-stat-label">Active Subjects</div>
          </div>
        </div>
        <div className="hod-stat-card" onClick={() => navigate('/hod/user-management')}>
          <div className="hod-stat-icon hod-stat-icon--purple"><UsersIcon /></div>
          <div>
            <div className="hod-stat-value">{stats.totalFaculty}</div>
            <div className="hod-stat-label">Faculty Members</div>
          </div>
        </div>
      </div>

      <div className="hod-content-layout">
        <div className="hod-card">
          <div className="hod-card-header">
            <h2>Quick Actions</h2>
          </div>
          
          <div className="hod-quick-actions">
            
            <div className="hod-action-card" onClick={() => navigate('/hod/subjects')}>
              <div className="hod-action-icon hod-action-icon--blue"><BookIcon /></div>
              <div className="hod-action-title">Manage Subjects</div>
              <div className="hod-action-desc">Add, assign, and customize subject definitions and syllabus sections.</div>
            </div>

            <div className="hod-action-card" onClick={() => navigate('/hod/templates?tab=create')}>
              <div className="hod-action-icon hod-action-icon--purple"><ClipboardIcon /></div>
              <div className="hod-action-title">Create Template</div>
              <div className="hod-action-desc">Define structure, total marks, and questions criteria for examination papers.</div>
            </div>

            <div className="hod-action-card" onClick={() => navigate('/hod/templates?tab=extract')}>
              <div className="hod-action-icon hod-action-icon--amber"><FileTextIcon /></div>
              <div className="hod-action-title">Extract Template</div>
              <div className="hod-action-desc">Upload existing question paper PDF to automatically extract template configurations.</div>
            </div>

            <div className="hod-action-card" onClick={() => navigate('/hod/user-management')}>
              <div className="hod-action-icon hod-action-icon--blue"><UsersIcon /></div>
              <div className="hod-action-title">Manage Users</div>
              <div className="hod-action-desc">Activate or deactivate moderators, SMEs, and examiners in your department.</div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default HODDashboard;
